import React, { useState, useEffect, useCallback } from "react"
import { Button, Col, Flex, Form, Row } from "antd"
import { serialize } from "object-to-formdata"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { Play } from "react-feather"

// helpers
import { compressFile } from "helpers/compress"
// hooks
import useFormErrors from "hooks/useAntdFormErrors"
// layouts
import { ContentActionButtons } from "layoutsV2/pro/PersonalInfoLayout/ContentActionButtons"
import { ContentHeader } from "layoutsV2/pro/PersonalInfoLayout/ContentHeader"
import { useSaveContext } from "layoutsV2/pro/PersonalInfoLayout/SaveContext"
// store
import { guideSelector, updateGuide, updateUser } from "store/user"
// themes
import { spacings, colors } from "themes/variables"
// ui
import { CustomForm } from "ui/Form"
import { CustomInput, TextArea } from "ui/Input"
// components
import { AddAvatar } from "./AddAvatar"
import { ProfilePreview } from "ui/PersonalInfo/ProfilePreview"
import { receiveErrors } from "modules/errors/reducer"
import { useDevice } from "hooks/useDevice"
import { Text } from "ui/Typography"
import styles from "./ProfileInformation.module.css"
import { AiEnhancementModal } from "ui/AiEnhancementModal"
import { Icon } from "ui/Icon"
export const ProfileInformation = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const [descriptionValue, setDescriptionValue] = useState("")
  const [profileDescription, setProfileDescription] = useState("")
  const { id, display_name, company_name, website, description, ...guide } = useSelector(guideSelector)
  const user = useSelector((store) => store.user)
  const [showPreview, setShowPreview] = useState(false)
  const { isMobile } = useDevice()
  const [showAiModal, setShowAiModal] = useState(false)
  const { registerSaveCallback, unregisterSaveCallback } = useSaveContext()

  const onFinish = useCallback((formData, callback) => {
  const serializedFormData = serialize({ guide: formData }, { indices: true })
  dispatch(updateGuide(id, serializedFormData)).then(callback)
}, [id, dispatch])

  // Create save callback for sidebar navigation
  const saveFormCallback = useCallback((onComplete) => {
    if (form.isFieldsTouched()) {
      const values = form.getFieldsValue()
      onFinish(values, onComplete)
    } else {
      if (onComplete) onComplete()
    }
  }, [form, onFinish])

  // Register save callback on mount, unregister on unmount
  useEffect(() => {
    registerSaveCallback(saveFormCallback)
    return () => {
      unregisterSaveCallback()
    }
  }, [registerSaveCallback, unregisterSaveCallback, saveFormCallback])

  const handleImageChange = async (e) => {
    const avatar = await compressFile(e.target.files[0])
    const formData = serialize({ avatar })
    if (avatar) dispatch(updateUser(formData))
    dispatch(
      receiveErrors({
        data: { message: { avatarPhoto: [] } },
        showToast: false
      })
    )
  }

  const handlePreviewToggle = () => setShowPreview((prev) => !prev)

  useFormErrors(form)
  useEffect(() => {
    form.setFieldsValue({ description: descriptionValue })
  }, [descriptionValue, form.setFieldsValue])
  useEffect(() => {
    if (description) {
      setDescriptionValue(description)
    }
  }, [description])

  return (
    <>
      <Row>
        <Col lg={16} md={24} xs={24} span={16}>
          <Row justify="space-between" align="middle" gutter={spacings.XS}>
            <ContentHeader title="Profile Information" />
            {!isMobile && (
              <Button color="primary" size="large" variant="outlined" onClick={handlePreviewToggle} icon={<Play size={16} />}>
                {t("global.preview")}
              </Button>
            )}
          </Row>
          <CustomForm
            form={form}
            name="profile-information"
            layout="vertical"
            initialValues={{
              display_name,
              company_name,
              website,
              description
            }}
          >
            <Form.Item
              name="avatarPhoto"
              label={t("guide.profile_image")}
              required={true}
              rules={[{ required: true, message: t("form_validation.cannot_be_blank") }]}
              className="mb-4xl"
            >
              <AddAvatar avatar={user.avatar} handleImageChange={handleImageChange} />
            </Form.Item>
            <Flex gap={spacings.S} vertical={isMobile}>
              <Form.Item
                label={t("guide.display_name")}
                name="display_name"
                required={true}
                rules={[{ required: true, message: t("form_validation.cannot_be_blank") }]}
                className="flex-grow-1"
              >
                <CustomInput placeholder={t("guide.display_name")} />
              </Form.Item>
              <Form.Item
                label={t("guide.company_name")}
                required={true}
                rules={[{ required: true, message: t("form_validation.cannot_be_blank") }]}
                name="company_name"
                className="flex-grow-1"
              >
                <CustomInput placeholder={t("guide.company_name")} />
              </Form.Item>
            </Flex>
            <Form.Item label={t("guide.website")} name="website" className="flex-grow-1">
              <CustomInput placeholder="https://" />
            </Form.Item>
            {/* Profile Description with AI Enhance Button */}
            <Form.Item
              name="description"
              className="flex-grow-1"
            >
              <Flex justify="space-between" align="center" className="mb-s w-full">
                <Text className={styles.descriptionLabel}>{t("guide.description")}*</Text>
                <Button
                  color="primary"
                  variant="solid"
                  icon={<Icon name="aiEnhancement" color={descriptionValue.length < 25 ? colors.textPrimary : colors.disabled} />}
                  disabled={descriptionValue.length < 25}
                  onClick={() => setShowAiModal(true)}
                >
                  {t("pro.ai_enhancement.button_text")}
                </Button>
              </Flex>

              <TextArea
                placeholder={t("guide.description_placeholder")}
                style={{ height: 147 }}
                value={descriptionValue}
                onChange={e => setDescriptionValue(e.target.value)}
              />
            </Form.Item>
          </CustomForm>
          <ContentActionButtons form={form} onFinish={onFinish} />
        </Col>
      </Row>
      <ProfilePreview
        show={showPreview}
        tab="about"
        guide={{
          ...guide,
          avatar: user.avatar,
          display_name: form.getFieldValue("display_name"),
          description: descriptionValue
        }}
        onClose={handlePreviewToggle}
      />
      {showAiModal && (
        <AiEnhancementModal
          open={showAiModal}
          onCancel={() => {
            setShowAiModal(false)
            setProfileDescription('')
          }}
          profileDescription={profileDescription}
          description={descriptionValue}
          setDescription={setDescriptionValue}
          setProfileDescription={setProfileDescription}
          onOk={() => {
            setShowAiModal(false)
          }}
          text={t("pro.ai_enhancement.form.textarea_label")}
          modalTitle={t("pro.ai_enhancement.modal_title")}
          messages={t("pro.ai_enhancement.form.continue_refinement_info")}
          refinementTooltipTitle={t("pro.ai_enhancement.form.continue_refinement_details")}
          improveWithAITooltipTitle={t("pro.ai_enhancement.form.ai_info")}
        />
      )}
    </>
  )
}
